import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import { api, ApiError } from '../../lib/api';

const inputClasses =
  'w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const labelClasses = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';

function slugify(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export default function AccountForm({ open, onClose, onSaved, initial }) {
  const editing = Boolean(initial);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    siteUrl: '',
    cfZoneId: '',
    auditScore: '',
    auditPdfUrl: '',
  });
  const [slugDirty, setSlugDirty] = useState(false);
  // Renaming is safe; changing the slug is not (it keys the GitHub repo
  // mapping for auto-resolve and the file-storage paths). Editing starts with
  // the slug locked and a deliberate step to change it.
  const [slugUnlocked, setSlugUnlocked] = useState(false);
  const [fieldError, setFieldError] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [saving, setSaving] = useState(false);
  const initialLoadedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      initialLoadedRef.current = false;
      return;
    }
    if (initial) {
      setForm({
        name: initial.name || '',
        slug: initial.slug || '',
        siteUrl: initial.siteUrl || '',
        cfZoneId: initial.cfZoneId || '',
        auditScore: initial.auditScore != null ? String(initial.auditScore) : '',
        auditPdfUrl: initial.auditPdfUrl || '',
      });
      setSlugDirty(true);
    } else {
      setForm({ name: '', slug: '', siteUrl: '', cfZoneId: '', auditScore: '', auditPdfUrl: '' });
      setSlugDirty(false);
    }
    setSlugUnlocked(false);
    setFieldError({});
    setGeneralError('');
    initialLoadedRef.current = true;
  }, [open, initial]);

  const onChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'name' && !slugDirty && !editing) {
        next.slug = slugify(value);
      }
      return next;
    });
    if (field === 'slug') setSlugDirty(true);
    setFieldError((prev) => ({ ...prev, [field]: undefined }));
    setGeneralError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFieldError({});
    setGeneralError('');

    // auditScore + auditPdfUrl are intentionally NOT in the payload — they
    // are now derived fields, auto-synced by the audit runner whenever a
    // new audit completes (functions/_lib/audit-runner.js writes them
    // alongside the audits row insert). Letting users edit them by hand
    // just created drift until the next audit overwrote it.
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim().toLowerCase(),
      siteUrl: form.siteUrl.trim() || null,
      cfZoneId: form.cfZoneId.trim() || null,
    };

    try {
      let result;
      if (editing) {
        result = await api.put(`/api/admin/accounts/${initial.id}`, payload);
      } else {
        result = await api.post('/api/admin/accounts', payload);
      }
      setSaving(false);
      onSaved?.(result.account);
      onClose?.();
    } catch (err) {
      setSaving(false);
      if (err instanceof ApiError) {
        if (err.data?.field) {
          setFieldError({ [err.data.field]: err.message });
        } else {
          setGeneralError(err.message || 'Failed to save account');
        }
      } else {
        setGeneralError('Network error, please try again.');
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      title={editing ? 'Edit account' : 'New account'}
      maxWidth="max-w-xl"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="account-form"
            disabled={saving}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                Saving…
              </span>
            ) : (
              editing ? 'Save changes' : 'Create account'
            )}
          </button>
        </div>
      }
    >
      <form id="account-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {generalError && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {generalError}
          </div>
        )}
        <div>
          <label className={labelClasses}>Account name *</label>
          <input
            type="text"
            value={form.name}
            onChange={onChange('name')}
            className={inputClasses}
            placeholder="P.S. 170X"
            autoFocus
            required
          />
          {fieldError.name && <p className="mt-1 text-[11px] text-red-400">{fieldError.name}</p>}
        </div>
        <div>
          <label className={labelClasses} htmlFor="account-slug">Slug *</label>
          <div className="flex gap-2">
            <input
              id="account-slug"
              type="text"
              value={form.slug}
              onChange={onChange('slug')}
              className={`${inputClasses} ${editing && !slugUnlocked ? 'opacity-60' : ''}`}
              placeholder="ps170x"
              required
              readOnly={editing && !slugUnlocked}
              aria-describedby="account-slug-help"
            />
            {editing && !slugUnlocked && (
              <button type="button" onClick={() => setSlugUnlocked(true)} className="px-3 rounded-lg border border-white/[0.08] bg-white/[0.02] text-[12px] text-gray-300 hover:text-white hover:border-white/[0.15] whitespace-nowrap" data-testid="unlock-slug">
                Change slug
              </button>
            )}
          </div>
          <p id="account-slug-help" className={`mt-1 text-[11px] ${editing && slugUnlocked ? 'text-amber-300/90' : 'text-gray-600'}`}>
            {editing && slugUnlocked
              ? 'Changing the slug breaks the auto-resolve repo mapping and the file paths under it, and is refused while client users exist. Rename the account instead unless you also update those.'
              : editing
                ? 'Renaming the account never changes the slug. The slug keys the auto-resolve repo mapping and file paths.'
                : 'Lowercase letters, numbers, and hyphens. Used in URLs and R2 paths.'}
          </p>
          {fieldError.slug && <p className="mt-1 text-[11px] text-red-400">{fieldError.slug}</p>}
        </div>
        <div>
          <label className={labelClasses}>Live site URL</label>
          <input
            type="url"
            value={form.siteUrl}
            onChange={onChange('siteUrl')}
            className={inputClasses}
            placeholder="https://ps170x.org"
          />
          {fieldError.siteUrl && <p className="mt-1 text-[11px] text-red-400">{fieldError.siteUrl}</p>}
        </div>
        <div>
          <label className={labelClasses}>Cloudflare zone ID</label>
          <input
            type="text"
            value={form.cfZoneId}
            onChange={onChange('cfZoneId')}
            className={`${inputClasses} font-mono text-[12px]`}
            placeholder="e.g. 7a8b9c0d1e2f3g4h5i6j7k8l9m0n1o2p"
          />
          <p className="mt-1 text-[11px] text-gray-600">
            Used to pull this site's analytics. Find under the zone Overview in Cloudflare.
          </p>
          {fieldError.cfZoneId && <p className="mt-1 text-[11px] text-red-400">{fieldError.cfZoneId}</p>}
        </div>
        {/* Audit summary — read-only. Auto-updated by the audit runner
            on every new audit (axe + WAVE in parallel). To refresh,
            trigger a new audit from the Audits tab on this account. */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Latest audit</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4">
            <div>
              <div className="text-[11px] text-gray-500 mb-1">WCAG score</div>
              <div className="text-[14px] text-white font-semibold">
                {form.auditScore !== '' ? `${form.auditScore} / 100` : <span className="text-gray-600 font-normal">—</span>}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-gray-500 mb-1">Audit PDF</div>
              {form.auditPdfUrl ? (
                <a
                  href={form.auditPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] text-[#B0FC38] hover:text-white truncate block"
                  title={form.auditPdfUrl}
                >
                  {form.auditPdfUrl}
                </a>
              ) : (
                <div className="text-[12px] text-gray-600">No audit PDF yet.</div>
              )}
            </div>
          </div>
          <div className="mt-2 text-[10.5px] text-gray-600 leading-relaxed">
            Auto-updated on every audit. To refresh, run a new audit from the Audits tab on this account.
          </div>
        </div>
      </form>
    </Modal>
  );
}
